import { API_BASE_URL } from '../config/app-config';

const API_ENDPOINTS = {
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    GOOGLE: '/auth/google',
    LOGOUT: '/auth/logout',
    ME: '/auth/me',
    UPDATE_PROFILE: '/auth/profile',
    CHANGE_PASSWORD: '/auth/password',
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    WATCHLIST: '/auth/watchlist',
    FAVORITES: '/auth/favorites',
    WATCHED: '/auth/watched',
    RATINGS: '/auth/ratings',
    STATS: '/auth/stats',
  },
  COMMENTS: '/comments',
};

class AuthService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    this.user = null;
    this.isInitialized = false;
    this.initPromise = null;
    
    // Request cache to prevent duplicate calls
    this.requestCache = new Map();
    this.cacheTimeout = 30000; // 30 seconds
  }

  // Get authorization header without Content-Type for GET requests
  getAuthHeaders(method = 'GET') {
    const headers = {};
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    // Only add Content-Type for requests that send data
    if (method !== 'GET' && method !== 'HEAD' && method !== 'DELETE') {
      headers['Content-Type'] = 'application/json';
    }
    
    return headers;
  }

  // Check if cached response is still valid
  isCacheValid(cacheEntry) {
    return Date.now() - cacheEntry.timestamp < this.cacheTimeout;
  }

  // Make authenticated API call with optimized headers
  async makeAuthenticatedRequest(url, options = {}) {
    const method = options.method || 'GET';
    const cacheKey = `${method}:${url}:${options.body || ''}`;
    
    // Check cache for GET requests
    if (method === 'GET') {
      const cached = this.requestCache.get(cacheKey);
      if (cached && this.isCacheValid(cached)) {
        return cached.data;
      }
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      cache: 'no-store', // Never use browser HTTP cache for authenticated requests
      headers: {
        ...this.getAuthHeaders(method),
        ...options.headers,
      },
    });

    // Handle token expiration
    if (response.status === 401) {
      this.logout();
      throw new Error('Prisijungimo sesija baigėsi');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Serverio klaida');
    }

    const data = await response.json();
    
    // Cache GET requests
    if (method === 'GET') {
      this.requestCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
    }

    return data;
  }

  // Register new user
  async register(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registracijos klaida');
      }

      // Store token and user data
      this.token = data.data.token;
      this.user = data.data.user;
      localStorage.setItem('authToken', this.token);
      localStorage.setItem('user', JSON.stringify(this.user));

      return { success: true, user: this.user, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Login user
  async login(credentials) {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Prisijungimo klaida');
      }

      // Store token and user data
      this.token = data.data.token;
      this.user = data.data.user;
      localStorage.setItem('authToken', this.token);
      localStorage.setItem('user', JSON.stringify(this.user));

      // Clear request cache on login
      this.requestCache.clear();

      return { success: true, user: this.user, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Login with Google
  async loginWithGoogle(credential) {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.GOOGLE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Google prisijungimo klaida');
      }

      // Store token and user data
      this.token = data.data.token;
      this.user = data.data.user;
      localStorage.setItem('authToken', this.token);
      localStorage.setItem('user', JSON.stringify(this.user));

      // Clear request cache on login
      this.requestCache.clear();

      return { success: true, user: this.user, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Logout user
  async logout() {
    try {
      if (this.token) {
        // Call backend logout endpoint
        await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.LOGOUT, {
          method: 'POST',
        }).catch(() => {
          // Ignore errors from logout endpoint
        });
      }
    } finally {
      // Always clear local data
      this.token = null;
      this.user = null;
      this.isInitialized = false;
      this.initPromise = null;
      this.requestCache.clear();
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }

  // Get current user from backend with deduplication
  async getCurrentUser() {
    try {
      if (!this.token) {
        throw new Error('Nėra prisijungimo žetono');
      }

      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.ME);
      this.user = data.data.user;
      localStorage.setItem('user', JSON.stringify(this.user));
      
      return this.user;
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  // Update user profile
  async updateProfile(profileData) {
    try {
      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.UPDATE_PROFILE, {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      this.user = { ...this.user, ...data.data.user };
      localStorage.setItem('user', JSON.stringify(this.user));

      return { success: true, user: this.user, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Change password
  async changePassword(passwordData) {
    try {
      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        method: 'PUT',
        body: JSON.stringify(passwordData),
      });

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Add to watchlist
  async addToWatchlist(itemId, mediaType) {
    try {
      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.WATCHLIST, {
        method: 'POST',
        body: JSON.stringify({ itemId, mediaType }),
      });

      if (this.user) {
        this.user.watchlist = data.data.watchlist;
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Remove from watchlist
  async removeFromWatchlist(itemId, mediaType) {
    try {
      const data = await this.makeAuthenticatedRequest(
        `${API_ENDPOINTS.AUTH.WATCHLIST}/${itemId}/${mediaType}`,
        { method: 'DELETE' }
      );

      if (this.user) {
        this.user.watchlist = data.data.watchlist;
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Add to favorites
  async addToFavorites(itemId, mediaType) {
    try {
      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.FAVORITES, {
        method: 'POST',
        body: JSON.stringify({ itemId, mediaType }),
      });

      if (this.user) {
        this.user.favorites = data.data.favorites;
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Remove from favorites
  async removeFromFavorites(itemId, mediaType) {
    try {
      const data = await this.makeAuthenticatedRequest(
        `${API_ENDPOINTS.AUTH.FAVORITES}/${itemId}/${mediaType}`,
        { method: 'DELETE' }
      );

      if (this.user) {
        this.user.favorites = data.data.favorites;
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Add to watched
  async addToWatched(itemId, mediaType, rating = null, comment = '') {
    try {
      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.WATCHED, {
        method: 'POST',
        body: JSON.stringify({ itemId, mediaType, rating, comment }),
      });

      if (this.user) {
        this.user.watched = data.data.watched;
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Remove from watched
  async removeFromWatched(itemId, mediaType) {
    try {
      const data = await this.makeAuthenticatedRequest(
        `${API_ENDPOINTS.AUTH.WATCHED}/${itemId}/${mediaType}`,
        { method: 'DELETE' }
      );

      if (this.user) {
        this.user.watched = data.data.watched;
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Add/Update rating
  async rateItem(itemId, mediaType, rating) {
    try {
      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.RATINGS, {
        method: 'POST',
        body: JSON.stringify({ itemId, mediaType, rating }),
      });

      if (this.user) {
        this.user.ratings = data.data.ratings;
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Get user statistics
  async getUserStats() {
    try {
      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.STATS);
      return data.data.stats;
    } catch (error) {
      throw error;
    }
  }

  // Resend email verification
  async resendEmailVerification() {
    try {
      const data = await this.makeAuthenticatedRequest(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        method: 'POST',
      });

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  // Verify email with token
  async verifyEmail(token) {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.VERIFY_EMAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Email verification failed');
      }

      // Update local user object with the verified user data from backend
      if (data.data && data.data.user) {
        // Use the updated user object from backend response
        this.user = { ...this.user, ...data.data.user };
        localStorage.setItem('user', JSON.stringify(this.user));
      } else if (this.user) {
        // Fallback: manually update local user object if backend doesn't return user data
        this.user.isEmailVerified = true;
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      return { success: true, message: data.message };
    } catch (error) {
      throw error;
    }
  }

  async postComment(movieId, mediaType, rating, comment) {
    try {
      const data = await this.makeAuthenticatedRequest(
        `${API_ENDPOINTS.COMMENTS}/${mediaType}/${movieId}`,
        {
          method: 'POST',
          body: JSON.stringify({ rating, comment }),
        }
      );
      return data.data.comment;
    } catch (error) {
      throw error;
    }
  }

  async deleteComment(commentId) {
    try {
      await this.makeAuthenticatedRequest(
        `${API_ENDPOINTS.COMMENTS}/${commentId}`,
        { method: 'DELETE' }
      );
      return { success: true };
    } catch (error) {
      throw error;
    }
  }

  async voteComment(commentId, voteType) {
    try {
      const data = await this.makeAuthenticatedRequest(
        `${API_ENDPOINTS.COMMENTS}/${commentId}/vote`,
        {
          method: 'POST',
          body: JSON.stringify({ voteType }),
        }
      );
      return data.data;
    } catch (error) {
      throw error;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  // Get current user data
  getUser() {
    return this.user;
  }

  // Get current token
  getToken() {
    return this.token;
  }

  // Initialize auth state from localStorage with deduplication
  async initializeAuth() {
    // Prevent multiple initialization attempts
    if (this.isInitialized || this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.performInitialization();
    return this.initPromise;
  }

  async performInitialization() {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        this.token = storedToken;
        this.user = JSON.parse(storedUser);

        // Verify token is still valid by getting fresh user data
        // But don't fail initialization if this fails
        try {
          await this.getCurrentUser();
        } catch (error) {
          // Token is invalid, clear auth state
          this.logout();
        }
      }
    } catch (error) {
      this.logout();
    } finally {
      this.isInitialized = true;
      this.initPromise = null;
    }
  }

  // Helper methods for checking user state
  isInWatchlist(itemId, mediaType) {
    return this.user?.watchlist?.some(
      item => item.itemId === itemId && item.mediaType === mediaType
    ) || false;
  }

  isInFavorites(itemId, mediaType) {
    return this.user?.favorites?.some(
      item => item.itemId === itemId && item.mediaType === mediaType
    ) || false;
  }

  isWatched(itemId, mediaType) {
    return this.user?.watched?.some(
      item => item.itemId === itemId && item.mediaType === mediaType
    ) || false;
  }

  getWatchedItem(itemId, mediaType) {
    return this.user?.watched?.find(
      item => item.itemId === itemId && item.mediaType === mediaType
    ) || null;
  }

  getUserRating(itemId, mediaType) {
    const rating = this.user?.ratings?.find(
      item => item.itemId === itemId && item.mediaType === mediaType
    );
    return rating?.rating || null;
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService; 